import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useColorMode } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { t } from 'i18next';
import 'react-horizontal-scrolling-menu/dist/styles.css';

import {
  EVENT_GOODS_ITEM_TYPES,
  filterGoodsByCategory,
  filterGoodsBySelectedItems,
  getApiCategory
} from '@pocapan/shared/utils/EventGoodsHelper';
import { useQueryParams } from '@pocapan/shared/utils/useQueryParams';
import baseUrl from '@pocapan/shared/utils/baseUrl';
import getToken from '@pocapan/shared/utils/getToken';

import StickyHeader from '../../../components/StickyHeader';
import { useEventGoodsQuery } from '../../../components/api/queries/useEventGoodsQuery';
import type { ApiV1EventGoods } from '../../../components/api/queries/useEventGoodsQuery';
import CardGoods from '../../../presentation/components/card/CardGoods';
import ReportEventGoodsTypeBottomDrawer from '../../../components/drawer/ReportEventGoodsTypeBottomDrawer';
import { ReportFootprintBreadcrumb } from '../../../components/report/ReportFootprintBreadcrumb';
import StepComponent from '../../../components/steps/StepComponent';

import { Button } from '@peacecover/component';
import { GOODS_TYPE_ITEMS } from '../../../constants/goodsTypeItem';
import GoodsTypeConfirmPopup from '../../../components/popup/GoodsTypeConfirmPopup';
import HorizontalScroll from '../../../presentation/my-page/components/HorizontalScroll';

export default function ReportEventGoodsTypePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { queryParams } = useQueryParams();
  const eventId = queryParams.get('eventId');
  const selectedItems = location.state?.selectedItems ?? [];

  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedGoods, setSelectedGoods] = useState<string[]>([]);
  const [goodsTypeMap, setGoodsTypeMap] = useState<Record<string, string[]>>({});
  const [isShowBottomDrawer, setIsShowBottomDrawer] = useState(false);
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep] = useState(0);

  // API에서 굿즈 리스트 가져오기
  const { data: eventGoods } = useEventGoodsQuery(getApiCategory(selectedCategory));

  const { colorMode } = useColorMode();

  const handleSelectItems = (id: string) => {
    setSelectedGoods((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleApplyGoodsTypes = (selectedTypes: string[]) => {
    if (selectedGoods.length === 0) return;
    setGoodsTypeMap((prev) => {
      const next = { ...prev };
      selectedGoods.forEach((key) => {
        next[key] = selectedTypes;
      });
      return next;
    });
  };

  // 다중 선택된 굿즈들의 초기 타입(교집합) 계산
  const getInitialSelectedGoodsTypes = (): string[] => {
    if (selectedGoods.length === 0) return [];
    if (selectedGoods.length === 1) return goodsTypeMap[selectedGoods[0]] || [];
    const [first, ...rest] = selectedGoods;
    const base = new Set((goodsTypeMap[first] || []).slice());
    for (const key of rest) {
      const types = new Set(goodsTypeMap[key] || []);
      for (const v of Array.from(base)) {
        if (!types.has(v)) base.delete(v);
      }
    }
    return Array.from(base);
  };

  // 특전 타입 ID를 이름으로 변환하는 함수
  const getGoodsTypeNames = (typeIds: string[]): string[] => {
    return typeIds.map((id) => {
      const item = GOODS_TYPE_ITEMS.find((item) => item.id === id);
      return item ? item.name : 'base';
    });
  };

  // 확인 팝업용: type별 그룹 데이터 구성
  const goodsKeyToName: Record<string, string> = (eventGoods || []).reduce(
    (acc: Record<string, string>, g: ApiV1EventGoods) => {
      acc[g.key] = g.name;
      return acc;
    },
    {}
  );

  const filledGoodsTypeMap: Record<string, string[]> = selectedItems.reduce(
    (acc: Record<string, string[]>, key: string) => {
      acc[key] = goodsTypeMap[key] && goodsTypeMap[key].length > 0 ? goodsTypeMap[key] : ['base'];
      return acc;
    },
    {}
  );

  const updateGoodsType = async () => {
    if (!eventId) return;
    // API 요청용 페이로드 형태로 변환: [{ key, gift_types }]
    setIsLoading(true);
    const payload = Object.entries(filledGoodsTypeMap).map(([key, gift_types]) => ({
      key,
      gift_types
    }));

    try {
      const response = await fetch(`${baseUrl()}/api/v1/events/${eventId}/add_goods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        navigate(`/report-location/events/add-details?${queryParams.toString()}`);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const groupedByType: Record<string, string[]> = Object.entries(filledGoodsTypeMap).reduce(
    (acc: Record<string, string[]>, [key, types]) => {
      types.forEach((t) => {
        if (!acc[t]) acc[t] = [];
        acc[t].push(goodsKeyToName[key] || key);
      });
      return acc;
    },
    {}
  );

  return (
    <>
      <Helmet>
        <title>{t('ReportEventGoodsTypePage.pageName', { defaultValue: '제보하기' })}</title>
      </Helmet>
      <StickyHeader
        title={t('ReportEventGoodsTypePage.pageName', { defaultValue: '제보하기' }) as string}
        alwaysShowTitle={true}
        showBackButton={true}
        showTitle={true}
        onClickBackButton={() => {
          navigate(-1);
        }}
      />
      <div className="flex flex-col pt-[calc(54px+env(safe-area-inset-top))] pb-[calc(128px+env(safe-area-inset-bottom))]">
        <div className="px-5 py-[10px]">
          <StepComponent currentStep={currentStep} nextStep={1} maxStep={5} title="">
            <div className="px-1 flex flex-col gap-[18px]">
              <div className="mx-[-24px]">
                <ReportFootprintBreadcrumb />
              </div>
              <div className="flex flex-col gap-[14px]">
                <div className="text-[26px] font-bold whitespace-pre-wrap text-text-main-lv1 leading-tight">
                  {t('ReportEventGoodsTypePage.title', {
                    defaultValue: '굿즈별로 어떤 특전인지\n선택해주세요!'
                  })}
                </div>
                <div className="text-[14px] text-text-main-lv4">
                  {t('ReportEventGoodsTypePage.description', {
                    defaultValue: '설정하지 않으면 기본 특전으로 지정돼요.'
                  })}
                </div>
              </div>
            </div>
            <HorizontalScroll>
              <div className="flex flex-nowrap mt-5 gap-[4px]">
                {EVENT_GOODS_ITEM_TYPES?.map((item) => (
                  <button
                    id={`menuButton_${item.id}`}
                    key={item.id}
                    type={'button'}
                    className={`px-[12px] py-[6px] gap-[4px] text-[13px] font-medium rounded-full ${
                      item.id === selectedCategory
                        ? 'bg-primary-light dark:bg-primary-dark text-on-primary'
                        : 'bg-secondary-light dark:bg-secondary-dark text-on-secondary'
                    }`}
                    onClick={() => setSelectedCategory(item.id)}
                  >
                    {t(`ReportEventGoodsTypePage.itemTypes.${item.key}`)}
                  </button>
                ))}
              </div>
            </HorizontalScroll>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3 w-full py-4">
              {filterGoodsBySelectedItems(
                filterGoodsByCategory(eventGoods, selectedCategory),
                selectedItems
              ).map((goods) => {
                const isSelected = selectedGoods.includes(goods.key);
                const goodsTypeIds = goodsTypeMap[goods.key] || [];
                const goodsTypeNames = getGoodsTypeNames(goodsTypeIds);

                return (
                  <CardGoods
                    key={goods.key}
                    goodsKey={goods.key}
                    goodsName={goods.name}
                    isSelected={isSelected}
                    isShowCheckIcon={true}
                    showGoodsTypes={true}
                    goodsTypes={goodsTypeNames}
                    onClick={() => handleSelectItems(goods.key)}
                  />
                );
              })}
            </div>
          </StepComponent>

          <ReportEventGoodsTypeBottomDrawer
            isOpen={isShowBottomDrawer}
            initialSelectedGoodsTypes={getInitialSelectedGoodsTypes()}
            onApply={handleApplyGoodsTypes}
            onClose={() => {
              setSelectedGoods([]);
              setIsShowBottomDrawer(false);
            }}
          />
          {isConfirmPopupOpen && (
            <GoodsTypeConfirmPopup
              groupedByType={groupedByType}
              isLoading={isLoading}
              onConfirm={updateGoodsType}
              onClose={() => setIsConfirmPopupOpen(false)}
            />
          )}
        </div>
        <div
          className={`${
            colorMode === 'dark' ? 'darkBottomNavbar' : 'bottomNavbar'
          } bg-background-light dark:bg-background-dark text-center z-[30] flex gap-[11px] w-full`}
          style={{
            padding: '10px 20px calc(10px + env(safe-area-inset-bottom)) 20px'
          }}
        >
          <Button
            size="large"
            className="flex-1 min-w-0 text-[17px] font-semibold px-[34px]"
            disabled={selectedGoods.length === 0}
            onClick={() => setIsShowBottomDrawer(true)}
          >
            {t('ReportEventGoodsTypePage.selectGoodsTypes', {
              defaultValue: '선택한 굿즈의 특전 정하기'
            })}
          </Button>
          <Button
            size="large"
            btnColor="secondary"
            className="flex-shrink-0 text-[17px] font-semibold"
            disabled={isEmpty(goodsTypeMap)}
            onClick={() => setIsConfirmPopupOpen(true)}
          >
            {t('done')}
          </Button>
        </div>
      </div>
    </>
  );
}